import clsx from 'clsx';
import { type ChangeEvent, type ComponentProps, useMemo, useRef } from 'react';

import ArrowRightIcon from '~/shared/assets/icons/arrow-right.svg';
import Button from '~/shared/components/ButtonsV2';
import Switch from '~/shared/components/Switch';
import SearchBar from '~/shared/components/SearchBar';
import SimpleDropdown from '~/shared/components/SimpleDropdown';
import Typography from '~/shared/components/Typography';

import { isExplorationOrgAggregateAccount } from '../lib/explorationAccountUtils';
import { useInert } from '../hooks/useInert';
import {
  useExplorationActions,
  useExplorationData,
  useExplorationState,
  useCurrentAccountVolumes,
} from '../context/ExplorationContext';
import { collectTagKeys } from '../lib/filterVolumes';

import type { ExplorationSecondary, ViewMode } from '../types/exploration';

const secondaryLabels: Record<ExplorationSecondary, string> = {
  cluster: 'Infrastructure',
  region: 'Region',
  tag: 'Volume Tag',
  none: 'None',
};

const secondaryValues = Object.fromEntries(
  Object.entries(secondaryLabels).map(([value, label]) => [label, value])
) as Record<string, ExplorationSecondary>;

// Headless UI anchor sets maxHeight via `min(var(--anchor-max-height, 100vh), …)` inline.
// Applied to the outer panel (MenuItems) so the search bar + scrollable list together
// never exceed this height; the list itself scrolls independently beneath the search bar.
const topbarDropdownScrollClass = '[--anchor-max-height:490px] max-h-[var(--anchor-max-height)]';
const topbarDropdownListClass =
  'tag-key-dropdown-scrollbar overflow-y-auto px-3 pb-2 mr-2 !w-[clamp(306px,calc(306px+(100vw-1280px)*97/256),348px)] max-w-[348px]';

const topbarDropdownItemClass = 'min-w-0';

const dropdownClass =
  'shrink-0 !w-[clamp(160px,calc(160px+(100vw-1280px)*65/640),213px)] max-w-[213px] transition-[width] duration-200 ease-out';
const searchBarClass =
  'min-w-0 shrink !w-[clamp(240px,calc(240px+(100vw-1280px)*97/256),337px)] max-w-[337px] transition-[width] duration-200 ease-out';

type TopBarDropdownProps<T extends string> = Omit<
  ComponentProps<typeof SimpleDropdown<T>>,
  'portal'
>;

function ExplorationTopBarDropdown<T extends string>({
  menuItemClassName,
  ...props
}: TopBarDropdownProps<T>) {
  return (
    <SimpleDropdown
      portal
      menuItemClassName={clsx(topbarDropdownItemClass, menuItemClassName)}
      {...props}
    />
  );
}

function tabListClass() {
  return clsx('inline-flex bg-gray-200 !rounded-[4px] p-1');
}

function tabButtonClass(active: boolean) {
  return clsx(
    '!h-auto !px-3 !py-1 font-sans text-sm font-medium bg-transparent border-0 !rounded-[4px]',
    active
      ? '!bg-white !text-primary-900 hover:!bg-white hover:!text-primary-900 shadow-[0_1px_2px_rgba(7,16,49,0.04)] font-semibold'
      : '!text-gray-600 hover:!bg-transparent hover:!text-gray-600 font-medium'
  );
}

const viewTabs: Array<{ id: ViewMode; label: string }> = [
  { id: 'exploration', label: 'Opportunity' },
  { id: 'activation', label: 'Activation' },
];

export default function ExplorationTopBar() {
  const state = useExplorationState();
  const actions = useExplorationActions();
  const { initialLoading } = useExplorationData();
  const volumes = useCurrentAccountVolumes();
  const tagKeys = useMemo(() => collectTagKeys(volumes), [volumes]);
  const showByAccount = isExplorationOrgAggregateAccount(state.accountId);
  const barRef = useRef<HTMLDivElement>(null);
  useInert(barRef, initialLoading);

  return (
    <div
      ref={barRef}
      className="flex items-center gap-3.5 px-[30px] py-[18px] h-18 border-b border-primary-70 bg-white min-w-0 overflow-hidden row-start-1"
    >
      <Button
        type="button"
        variant="third"
        size="sm"
        icon={
          <ArrowRightIcon
            className={clsx('size-4.5', state.sidebarCollapsed ? '' : 'rotate-180')}
          />
        }
        className="!w-9 !h-9 !p-0 shrink-0 border-primary-70 text-gray-600 hover:text-primary-700"
        onClick={actions.toggleSidebar}
        title={state.sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
        aria-label={state.sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
      />
      <div className="h-full w-px border-r border-primary-70"></div>
      <div className={tabListClass()}>
        {viewTabs.map(({ id, label }) => (
          <Button
            key={id}
            type="button"
            variant="text"
            size="sm"
            className={tabButtonClass(state.view === id)}
            onClick={() => actions.setView(id)}
          >
            <Typography variant="p3" fontWeight={state.view === id ? 600 : 500} color="inherit">
              {label}
            </Typography>
          </Button>
        ))}
      </div>

      <div className="flex items-center gap-4 min-w-0">
        <div className="inline-flex items-center gap-1.5 shrink-0">
          <Typography variant="p3-reg" color="text-gray-600" className="!whitespace-nowrap">
            Group by
          </Typography>
          <ExplorationTopBarDropdown
            aria-label="Group by"
            className={dropdownClass}
            menuButtonClassName="!h-9"
            menuItemsClassName={dropdownClass}
            items={Object.values(secondaryLabels)}
            selectedItem={secondaryLabels[state.secondary]}
            onSelect={(label) => actions.setSecondary(secondaryValues[label])}
          />
        </div>

        {state.secondary === 'tag' && tagKeys.length > 0 && (
          <div className="inline-flex items-center gap-2 min-w-0">
            <Typography variant="p3-reg" color="text-gray-600" className="shrink-0">
              By
            </Typography>
            <ExplorationTopBarDropdown
              aria-label="Tag key"
              className={dropdownClass}
              menuButtonClassName="!h-9"
              menuItemClassName={'rounded-[8px] h-13!'}
              menuItemsClassName={topbarDropdownScrollClass}
              showItemsBorderBottom={false}
              menuListClassName={topbarDropdownListClass}
              items={tagKeys}
              selectedItem={state.tagKey || tagKeys[0]}
              displayLabel={state.tagKey || 'Select tag key'}
              isItemSelected={(item) => Boolean(state.tagKey) && item === state.tagKey}
              onSelect={(key) => actions.setTagKey(key)}
              searchable
              searchPlaceholder="Search"
              showItemTooltip
              tooltipIdPrefix="exploration-tag-key"
              itemTagVariant="volume"
            />
          </div>
        )}
      </div>

      {showByAccount && (
        <div className="inline-flex items-center gap-1.5 h-9">
          <Switch
            size="sm"
            checked={state.groupByAccount}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              actions.setGroupByAccount(e.target.checked)
            }
          />
          <Typography variant="caption" className="!whitespace-nowrap">
            By account
          </Typography>
        </div>
      )}

      <div className="flex items-center ml-auto min-w-0 shrink gap-2">
        <SearchBar
          size="sm"
          className={searchBarClass}
          inputClassName="placeholder:text-gray-600!"
          placeholder="Search"
          value={state.searchQuery}
          onChange={(e) => actions.setSearch(e.target.value)}
        />
      </div>
    </div>
  );
}
